import { useState, useEffect } from 'react';
import { adminApi } from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { formatCurrency, formatDate, getStatusStyle, getKYCTierInfo } from '../../lib/utils';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Search, User, ChevronRight } from 'lucide-react';

const AdminUsersPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await adminApi.getUsers({ limit: 100 });
      setUsers(res.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.full_name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.phone?.includes(query)
    );
  });

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Users
            </h1>
            <p className="text-muted-foreground">Manage platform users</p>
          </div>
        </div>
        
        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 bg-secondary border-white/10"
              data-testid="user-search"
            />
          </div>
        </div>
        
        {/* Users Table */}
        <div className="bg-card border border-white/10 rounded-xl overflow-hidden">
          {isLoading ? (
            <div className="p-4 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">User</th>
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">Type</th>
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">KYC</th>
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">Balance</th>
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => {
                    const tierInfo = getKYCTierInfo(user.kyc_tier);
                    const statusStyle = getStatusStyle(user.wallet_status);
                    
                    return (
                      <tr 
                        key={user.wallet_id} 
                        className="border-b border-white/10 hover:bg-white/5 transition-colors"
                        data-testid={`user-row-${user.wallet_id}`}
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                              <User className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">{user.full_name}</p>
                              <p className="text-sm text-muted-foreground">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="capitalize">{user.user_type}</span>
                        </td>
                        <td className="p-4">
                          <span className={`text-xs px-2 py-1 rounded-full ${tierInfo.bgColor} ${tierInfo.color}`}>
                            Tier {user.kyc_tier}
                          </span>
                        </td>
                        <td className="p-4 font-medium">
                          {formatCurrency(user.available_balance)}
                        </td>
                        <td className="p-4">
                          <span className={`text-xs px-2 py-1 rounded-full ${statusStyle.bg} ${statusStyle.text}`}>
                            {statusStyle.label}
                          </span>
                        </td>
                        <td className="p-4">
                          <button className="p-2 hover:bg-white/5 rounded-lg transition-colors">
                            <ChevronRight className="w-5 h-5 text-muted-foreground" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-card border border-white/10 rounded-xl p-4">
            <p className="text-2xl font-bold">{users.length}</p>
            <p className="text-sm text-muted-foreground">Total Users</p>
          </div>
          <div className="bg-card border border-white/10 rounded-xl p-4">
            <p className="text-2xl font-bold text-green-500">
              {users.filter(u => u.user_type === 'creator').length}
            </p>
            <p className="text-sm text-muted-foreground">Creators</p>
          </div>
          <div className="bg-card border border-white/10 rounded-xl p-4">
            <p className="text-2xl font-bold text-blue-500">
              {users.filter(u => u.user_type === 'merchant').length}
            </p>
            <p className="text-sm text-muted-foreground">Merchants</p>
          </div>
          <div className="bg-card border border-white/10 rounded-xl p-4">
            <p className="text-2xl font-bold text-yellow-500">
              {users.filter(u => u.kyc_tier === 3).length}
            </p>
            <p className="text-sm text-muted-foreground">Tier 3 Verified</p>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminUsersPage;
